import { create } from 'zustand'

export type ToastType   = 'success' | 'error' | 'info'
export type ToastAction = { label: string; onClick: () => void }

export interface ToastItem {
  id:      string
  message: string
  type:    ToastType
  action?: ToastAction
}

export type ToastOptions = {
  type?:       ToastType
  action?:     ToastAction
  durationMs?: number
}

interface ToastStore {
  toasts:      ToastItem[]
  addToast:    (message: string, opts?: ToastOptions) => void
  removeToast: (id: string) => void
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (message, opts = {}) => {
    const id       = Math.random().toString(36).slice(2, 9)
    const type     = opts.type ?? 'success'
    const duration = opts.durationMs ?? 3000
    set(s => ({ toasts: [...s.toasts, { id, message, type, action: opts.action }] }))
    setTimeout(() => {
      set(s => ({ toasts: s.toasts.filter(t => t.id !== id) }))
    }, duration)
  },
  removeToast: (id) => set(s => ({ toasts: s.toasts.filter(t => t.id !== id) })),
}))

/**
 * Backward-compatible overloads — every existing `showToast('msg')` /
 * `showToast('msg', 'error')` call site continues to work unchanged.
 * New callers can pass an options object with an `action` for undo toasts.
 */
export function showToast(message: string): void
export function showToast(message: string, type: ToastType): void
export function showToast(message: string, opts: ToastOptions): void
export function showToast(message: string, arg?: ToastType | ToastOptions) {
  if (arg === undefined) {
    useToastStore.getState().addToast(message)
  } else if (typeof arg === 'string') {
    useToastStore.getState().addToast(message, { type: arg })
  } else {
    useToastStore.getState().addToast(message, arg)
  }
}
